import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Dna, ShoppingCart, Info, Beaker, Settings, Target, Scissors, ChevronDown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PrimerDesignWizardFull = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart, getItemCount } = useCart();

  // Sequence input
  const [sequence, setSequence] = useState('');
  const [sequenceName, setSequenceName] = useState('');
  
  // Assay type
  const [assayType, setAssayType] = useState('primers_only');
  
  // Design parameters
  const [numResults, setNumResults] = useState(5);
  
  // Primer parameters
  const [primerParams, setPrimerParams] = useState({
    min_size: 18,
    opt_size: 20,
    max_size: 25,
    min_tm: 57.0,
    opt_tm: 60.0,
    max_tm: 63.0,
    min_gc: 40.0,
    opt_gc: 50.0,
    max_gc: 60.0,
    gc_clamp: 0,
  });
  
  // Probe parameters (Tm should be 5°C higher)
  const [probeParams, setProbeParams] = useState({
    min_size: 20,
    opt_size: 25,
    max_size: 30,
    min_tm: 65.0,
    opt_tm: 68.0,
    max_tm: 72.0,
    min_gc: 40.0,
    opt_gc: 50.0,
    max_gc: 60.0,
  });
  
  // Amplicon parameters
  const [ampliconParams, setAmpliconParams] = useState({
    min_size: 70,
    opt_size: 100,
    max_size: 150,
  });
  
  // Reaction conditions
  const [reactionConditions, setReactionConditions] = useState({
    mv_conc: 50.0,
    dv_conc: 3.0,
    dntp_conc: 0.8,
    primer_conc: 200.0,
    probe_conc: 200.0,
  });
  
  // Primer location variation
  const [varyForward, setVaryForward] = useState(0);
  const [varyReverse, setVaryReverse] = useState(0);
  
  // Advanced: Regions
  const [useIncludedRegion, setUseIncludedRegion] = useState(false);
  const [includedRegion, setIncludedRegion] = useState('');
  const [useExcludedRegions, setUseExcludedRegions] = useState(false);
  const [excludedRegions, setExcludedRegions] = useState('');
  const [useTargetRegions, setUseTargetRegions] = useState(false);
  const [targetRegions, setTargetRegions] = useState('');
  const [useExcludedProbeRegions, setUseExcludedProbeRegions] = useState(false);
  const [excludedProbeRegions, setExcludedProbeRegions] = useState('');
  
  // Advanced: Junction design
  const [useJunctions, setUseJunctions] = useState(false);
  const [junctionList, setJunctionList] = useState('');
  const [minOverlap3Prime, setMinOverlap3Prime] = useState(4);
  const [minOverlap5Prime, setMinOverlap5Prime] = useState(7);
  
  // Advanced: Force primer positions
  const [forceForwardStart, setForceForwardStart] = useState('');
  const [forceForwardEnd, setForceForwardEnd] = useState('');
  const [forceReverseStart, setForceReverseStart] = useState('');
  const [forceReverseEnd, setForceReverseEnd] = useState('');
  
  // Results
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Ordering state
  const [selectedPrimerSet, setSelectedPrimerSet] = useState(null);
  const [doubleQuencher, setDoubleQuencher] = useState(false);
  const [quantity, setQuantity] = useState(1);

  const parseRegionString = (str) => {
    // Parse "100-200, 300-400" to [[100, 100], [300, 100]]
    if (!str.trim()) return null;
    try {
      const pairs = str.split(',').map(s => s.trim());
      return pairs.map(pair => {
        const [start, end] = pair.split('-').map(n => parseInt(n.trim()));
        return [start, end - start + 1]; // [start, length]
      });
    } catch (e) {
      return null;
    }
  };

  const parseJunctionString = (str) => {
    // Parse "120, 255, 345" to [120, 255, 345]
    if (!str.trim()) return null;
    try {
      return str.split(',').map(n => parseInt(n.trim()));
    } catch (e) {
      return null;
    }
  };

  const handleDesign = async () => {
    if (!sequence || sequence.length < 50) {
      toast.error('Please enter a valid DNA sequence (minimum 50 bp)');
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        sequence: sequenceName ? `>${sequenceName}\n${sequence}` : sequence,
        sequence_name: sequenceName || 'Target',
        assay_type: assayType,
        num_results: numResults,
        
        // Primer params
        primer_min_size: primerParams.min_size,
        primer_opt_size: primerParams.opt_size,
        primer_max_size: primerParams.max_size,
        primer_min_tm: primerParams.min_tm,
        primer_opt_tm: primerParams.opt_tm,
        primer_max_tm: primerParams.max_tm,
        primer_min_gc: primerParams.min_gc,
        primer_opt_gc: primerParams.opt_gc,
        primer_max_gc: primerParams.max_gc,
        primer_gc_clamp: primerParams.gc_clamp,
        
        // Probe params (only if designing probes)
        probe_min_size: probeParams.min_size,
        probe_opt_size: probeParams.opt_size,
        probe_max_size: probeParams.max_size,
        probe_min_tm: probeParams.min_tm,
        probe_opt_tm: probeParams.opt_tm,
        probe_max_tm: probeParams.max_tm,
        probe_min_gc: probeParams.min_gc,
        probe_opt_gc: probeParams.opt_gc,
        probe_max_gc: probeParams.max_gc,
        
        // Amplicon
        amplicon_min_size: ampliconParams.min_size,
        amplicon_opt_size: ampliconParams.opt_size,
        amplicon_max_size: ampliconParams.max_size,
        
        // Reaction conditions
        mv_conc: reactionConditions.mv_conc,
        dv_conc: reactionConditions.dv_conc,
        dntp_conc: reactionConditions.dntp_conc,
        primer_conc: reactionConditions.primer_conc,
        probe_conc: reactionConditions.probe_conc,
        
        // Variation
        vary_forward_location: varyForward,
        vary_reverse_location: varyReverse,
      };
      
      // Add regions if specified
      if (useIncludedRegion && includedRegion) {
        const parsed = parseRegionString(includedRegion);
        if (parsed && parsed.length > 0) {
          requestData.included_region = parsed[0];
        }
      }
      
      if (useExcludedRegions && excludedRegions) {
        requestData.excluded_regions = parseRegionString(excludedRegions);
      }
      
      if (useTargetRegions && targetRegions) {
        requestData.target_regions = parseRegionString(targetRegions);
      }
      
      if (useExcludedProbeRegions && excludedProbeRegions) {
        requestData.excluded_regions_probe = parseRegionString(excludedProbeRegions);
      }
      
      // Add junctions if specified
      if (useJunctions && junctionList) {
        requestData.junction_list = parseJunctionString(junctionList);
        requestData.min_overlap_3prime = minOverlap3Prime;
        requestData.min_overlap_5prime = minOverlap5Prime;
      }
      
      // Add forced positions if specified
      if (forceForwardStart) requestData.force_forward_start = parseInt(forceForwardStart);
      if (forceForwardEnd) requestData.force_forward_end = parseInt(forceForwardEnd);
      if (forceReverseStart) requestData.force_reverse_start = parseInt(forceReverseStart);
      if (forceReverseEnd) requestData.force_reverse_end = parseInt(forceReverseEnd);

      const response = await axios.post(`${API_URL}/wizard/design`, requestData);
      
      setResults(response.data);
      if (response.data.success && response.data.primer_sets.length > 0) {
        toast.success(`Found ${response.data.primer_sets.length} primer set(s)!`);
      } else {
        toast.warning(response.data.message || 'No suitable primers found. Try adjusting parameters.');
      }
    } catch (error) {
      console.error('Design error:', error);
      toast.error(error.response?.data?.detail || 'Failed to design primers. Please check your sequence and try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = (primerSet) => {
    let totalPrice = 0;
    
    // Primers: €0.7 per base
    const forwardPrice = primerSet.forward_length * 0.7;
    const reversePrice = primerSet.reverse_length * 0.7;
    totalPrice = forwardPrice + reversePrice;
    
    // Probe if applicable
    if (primerSet.probe_sequence) {
      if (doubleQuencher) {
        totalPrice += 250; // Double quencher TaqMan probe
      } else {
        totalPrice += 200; // Standard TaqMan probe
      }
    }
    
    return totalPrice.toFixed(2);
  };

  const handleAddToCart = async (primerSet) => {
    if (!user) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }
    
    try {
      const price = parseFloat(calculatePrice(primerSet));
      
      const cartItem = {
        technology: 'PrimerDesignWizard',
        application: assayType,
        configuration: {
          sequence_name: results.sequence_name,
          rank: primerSet.rank,
          forward_primer: primerSet.forward_primer,
          forward_tm: primerSet.forward_tm,
          forward_gc: primerSet.forward_gc,
          forward_length: primerSet.forward_length,
          reverse_primer: primerSet.reverse_primer,
          reverse_tm: primerSet.reverse_tm,
          reverse_gc: primerSet.reverse_gc,
          reverse_length: primerSet.reverse_length,
          probe_sequence: primerSet.probe_sequence || null,
          probe_tm: primerSet.probe_tm || null,
          probe_type: primerSet.probe_sequence ? (doubleQuencher ? 'Double Quencher' : 'Standard') : null,
          amplicon_size: primerSet.amplicon_size,
          quality_score: primerSet.quality_score,
        },
        price: price,
        quantity: quantity,
      };

      await addToCart(cartItem);
      toast.success(`Added primer set ${primerSet.rank} to cart (€${price * quantity})`);
      setSelectedPrimerSet(null);
      setDoubleQuencher(false);
      setQuantity(1);
    } catch (error) {
      toast.error('Failed to add to cart');
      console.error(error);
    }
  };

  const loadExample = () => {
    setSequenceName('GAPDH_Example');
    setSequence('ATGGGGAAGGTGAAGGTCGGAGTCAACGGATTTGGTCGTATTGGGCGCCTGGTCACCAGGGCTGCTTTTAACTCTGGTAAAGTGGATATTGTTGCCATCAATGACCCCTTCATTGACCTCAACTACATGGTTTACATGTTCCAATATGATTCCACCCATGGCAAATTCCATGGCACCGTCAAGGCTGAGAACGGGAAGCTTGTCATCAATGGAAATCCCATCACCATCTTCCAGGAGCGAGATCCCTCCAAAATCAAGTGGGGCGATGCTGGCGCTGAGTACGTCGTGGAGTCCACTGGCGTCTTCACCACCATGGAGAAGGCTGGGGCTCATTTGCAGGGGGGAGCCAAAAGGGTCATCATCTCTGCCCCCTCTGCTGATGCCCCCATGTTCGTCATGGGTGTGAACCATGAGAAGTATGACAACAGCCTCAAGATCATCAGCAATGCCTCCTGCACCACCAACTGCTT');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Beaker className="w-6 h-6 text-blue-600" />
              <span className="font-semibold text-lg">PrimerDesignWizard</span>
            </div>
            <div className="flex items-center space-x-4">
              <Button 
                variant="outline" 
                onClick={() => navigate('/workflow')}
              >
                Change Application
              </Button>
              {user && (
                <Button onClick={() => navigate('/cart')} variant="outline" className="relative">
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Cart
                  {getItemCount() > 0 && (
                    <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {getItemCount()}
                    </span>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Info Button with Disclaimer */}
          <div className="flex justify-end">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Info className="h-4 w-4 mr-2" />
                  Info
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Legal Disclaimer</DialogTitle>
                  <DialogDescription className="text-sm text-gray-700 whitespace-pre-line pt-4">
                    {`PrimerDesignWizard incorporates Primer3 software (version 2.6.1) and Primer3Plus (version 3.3.0) developed by the Whitehead Institute for Biomedical Research, © Copyright 2018 Whitehead Institute for Biomedical Research, Steve Rozen. PrimerDesignWizard is not identical to Primer3 software distributed by the Whitehead Institute, which in no way endorses PrimerDesignWizard. Primer3's source code can be downloaded here.

With respect to the use of all PrimerDesignWizard and Primer3 and Primer3Plus software, the following conditions apply:

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNERS OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.`}
                  </DialogDescription>
                </DialogHeader>
              </DialogContent>
            </Dialog>
          </div>

          {/* Sequence Input */}
          <Card>
            <CardHeader>
              <CardTitle>1. Enter Target Sequence</CardTitle>
              <CardDescription>
                Paste your DNA sequence in FASTA format or plain text (minimum 50 bp)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="seq-name">Sequence Name (Optional)</Label>
                <Input
                  id="seq-name"
                  placeholder="e.g., GAPDH, Target_Gene_1"
                  value={sequenceName}
                  onChange={(e) => setSequenceName(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="sequence">DNA Sequence *</Label>
                <Textarea
                  id="sequence"
                  placeholder="Paste your DNA sequence here (A, T, G, C)..."
                  value={sequence}
                  onChange={(e) => setSequence(e.target.value.toUpperCase())}
                  rows={8}
                  className="mt-1 font-mono text-sm"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Length: {sequence.replace(/\s/g, '').length} bp
                </p>
              </div>

              <Button variant="outline" onClick={loadExample}>
                Load Example Sequence
              </Button>
            </CardContent>
          </Card>

          {/* Assay Type - Toggle Buttons */}
          <Card>
            <CardHeader>
              <CardTitle>2. Select Assay Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => setAssayType('primers_only')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    assayType === 'primers_only' 
                      ? 'border-blue-600 bg-blue-50' 
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="font-semibold text-gray-900">General PCR</div>
                  <div className="text-sm text-gray-600 mt-1">(Primers only)</div>
                </button>
                
                <button
                  onClick={() => setAssayType('primers_and_probe')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    assayType === 'primers_and_probe' 
                      ? 'border-blue-600 bg-blue-50' 
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="font-semibold text-gray-900">qPCR with Probe</div>
                  <div className="text-sm text-gray-600 mt-1">(2 Primers + Hydrolysis Probe)</div>
                </button>
                
                <button
                  onClick={() => setAssayType('intercalating_dyes')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    assayType === 'intercalating_dyes' 
                      ? 'border-blue-600 bg-blue-50' 
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="font-semibold text-gray-900">qPCR with Dyes</div>
                  <div className="text-sm text-gray-600 mt-1">(2 Primers for Int. Dyes as Sybr green)</div>
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Design Parameters - Collapsible (Default Closed) */}
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="design-params" className="border rounded-lg bg-white">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-lg">3. Design Parameters</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="basic">Basic</TabsTrigger>
                  <TabsTrigger value="advanced">Advanced</TabsTrigger>
                  <TabsTrigger value="regions">Regions & Junctions</TabsTrigger>
                </TabsList>

                {/* Basic Parameters */}
                <TabsContent value="basic" className="space-y-6 mt-4">
                  {/* Results to return */}
                  <div>
                    <Label>Number of Results to Return</Label>
                    <Input
                      type="number"
                      min="1"
                      max="50"
                      value={numResults}
                      onChange={(e) => setNumResults(parseInt(e.target.value))}
                      className="mt-1 w-32"
                    />
                    <p className="text-xs text-gray-500 mt-1">1-50 primer sets (default: 5)</p>
                  </div>

                  {/* Primer Parameters */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Primer Criteria</h3>
                    
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs">Size (bp)</Label>
                        <div className="flex gap-2 mt-1">
                          <div className="flex-1">
                            <Input
                              type="number"
                              placeholder="Min"
                              value={primerParams.min_size}
                              onChange={(e) => setPrimerParams({...primerParams, min_size: parseInt(e.target.value)})}
                            />
                          </div>
                          <div className="flex-1">
                            <Input
                              type="number"
                              placeholder="Opt"
                              value={primerParams.opt_size}
                              onChange={(e) => setPrimerParams({...primerParams, opt_size: parseInt(e.target.value)})}
                            />
                          </div>
                          <div className="flex-1">
                            <Input
                              type="number"
                              placeholder="Max"
                              value={primerParams.max_size}
                              onChange={(e) => setPrimerParams({...primerParams, max_size: parseInt(e.target.value)})}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs">Tm (°C)</Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="Min"
                            value={primerParams.min_tm}
                            onChange={(e) => setPrimerParams({...primerParams, min_tm: parseFloat(e.target.value)})}
                          />
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="Opt"
                            value={primerParams.opt_tm}
                            onChange={(e) => setPrimerParams({...primerParams, opt_tm: parseFloat(e.target.value)})}
                          />
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="Max"
                            value={primerParams.max_tm}
                            onChange={(e) => setPrimerParams({...primerParams, max_tm: parseFloat(e.target.value)})}
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs">GC (%)</Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="Min"
                            value={primerParams.min_gc}
                            onChange={(e) => setPrimerParams({...primerParams, min_gc: parseFloat(e.target.value)})}
                          />
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="Opt"
                            value={primerParams.opt_gc}
                            onChange={(e) => setPrimerParams({...primerParams, opt_gc: parseFloat(e.target.value)})}
                          />
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="Max"
                            value={primerParams.max_gc}
                            onChange={(e) => setPrimerParams({...primerParams, max_gc: parseFloat(e.target.value)})}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>3' GC Clamp (nt)</Label>
                        <Input
                          type="number"
                          value={primerParams.gc_clamp}
                          onChange={(e) => setPrimerParams({...primerParams, gc_clamp: parseInt(e.target.value)})}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Probe Parameters (only if designing probes) */}
                  {assayType === 'primers_and_probe' && (
                    <div className="space-y-4 pt-4 border-t">
                      <h3 className="font-semibold text-lg">Probe Criteria (TaqMan)</h3>
                      <p className="text-sm text-gray-600">Note: Probe Tm should be ~5°C higher than primer Tm</p>
                      
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs">Size (bp)</Label>
                          <div className="flex gap-2 mt-1">
                            <Input
                              type="number"
                              placeholder="Min"
                              value={probeParams.min_size}
                              onChange={(e) => setProbeParams({...probeParams, min_size: parseInt(e.target.value)})}
                            />
                            <Input
                              type="number"
                              placeholder="Opt"
                              value={probeParams.opt_size}
                              onChange={(e) => setProbeParams({...probeParams, opt_size: parseInt(e.target.value)})}
                            />
                            <Input
                              type="number"
                              placeholder="Max"
                              value={probeParams.max_size}
                              onChange={(e) => setProbeParams({...probeParams, max_size: parseInt(e.target.value)})}
                            />
                          </div>
                        </div>

                        <div>
                          <Label className="text-xs">Tm (°C)</Label>
                          <div className="flex gap-2 mt-1">
                            <Input
                              type="number"
                              step="0.1"
                              placeholder="Min"
                              value={probeParams.min_tm}
                              onChange={(e) => setProbeParams({...probeParams, min_tm: parseFloat(e.target.value)})}
                            />
                            <Input
                              type="number"
                              step="0.1"
                              placeholder="Opt"
                              value={probeParams.opt_tm}
                              onChange={(e) => setProbeParams({...probeParams, opt_tm: parseFloat(e.target.value)})}
                            />
                            <Input
                              type="number"
                              step="0.1"
                              placeholder="Max"
                              value={probeParams.max_tm}
                              onChange={(e) => setProbeParams({...probeParams, max_tm: parseFloat(e.target.value)})}
                            />
                          </div>
                        </div>

                        <div>
                          <Label className="text-xs">GC (%)</Label>
                          <div className="flex gap-2 mt-1">
                            <Input
                              type="number"
                              step="0.1"
                              placeholder="Min"
                              value={probeParams.min_gc}
                              onChange={(e) => setProbeParams({...probeParams, min_gc: parseFloat(e.target.value)})}
                            />
                            <Input
                              type="number"
                              step="0.1"
                              placeholder="Opt"
                              value={probeParams.opt_gc}
                              onChange={(e) => setProbeParams({...probeParams, opt_gc: parseFloat(e.target.value)})}
                            />
                            <Input
                              type="number"
                              step="0.1"
                              placeholder="Max"
                              value={probeParams.max_gc}
                              onChange={(e) => setProbeParams({...probeParams, max_gc: parseFloat(e.target.value)})}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Amplicon Parameters */}
                  <div className="space-y-4 pt-4 border-t">
                    <h3 className="font-semibold text-lg">Amplicon Size</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label>Minimum (bp)</Label>
                        <Input
                          type="number"
                          value={ampliconParams.min_size}
                          onChange={(e) => setAmpliconParams({...ampliconParams, min_size: parseInt(e.target.value)})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Optimum (bp)</Label>
                        <Input
                          type="number"
                          value={ampliconParams.opt_size}
                          onChange={(e) => setAmpliconParams({...ampliconParams, opt_size: parseInt(e.target.value)})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Maximum (bp)</Label>
                        <Input
                          type="number"
                          value={ampliconParams.max_size}
                          onChange={(e) => setAmpliconParams({...ampliconParams, max_size: parseInt(e.target.value)})}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Advanced Parameters */}
                <TabsContent value="advanced" className="space-y-6 mt-4">
                  {/* Reaction Conditions */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Reaction Conditions</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Monovalent Salt (Na+) (mM)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={reactionConditions.mv_conc}
                          onChange={(e) => setReactionConditions({...reactionConditions, mv_conc: parseFloat(e.target.value)})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Divalent Salt (Mg++) (mM)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={reactionConditions.dv_conc}
                          onChange={(e) => setReactionConditions({...reactionConditions, dv_conc: parseFloat(e.target.value)})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>dNTP Concentration (mM)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={reactionConditions.dntp_conc}
                          onChange={(e) => setReactionConditions({...reactionConditions, dntp_conc: parseFloat(e.target.value)})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Primer DNA Concentration (nM)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={reactionConditions.primer_conc}
                          onChange={(e) => setReactionConditions({...reactionConditions, primer_conc: parseFloat(e.target.value)})}
                          className="mt-1"
                        />
                      </div>
                      {assayType === 'primers_and_probe' && (
                        <div>
                          <Label>Probe DNA Concentration (nM)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={reactionConditions.probe_conc}
                            onChange={(e) => setReactionConditions({...reactionConditions, probe_conc: parseFloat(e.target.value)})}
                            className="mt-1"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Primer Location Variation */}
                  <div className="space-y-4 pt-4 border-t">
                    <h3 className="font-semibold text-lg">Primer Location Variation</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Vary Forward Primer Location</Label>
                        <div className="flex gap-2 mt-2">
                          {['Off', 'Low', 'Medium', 'High'].map((label, idx) => (
                            <button
                              key={idx}
                              onClick={() => setVaryForward(idx)}
                              className={`px-3 py-1 border rounded text-sm transition-all ${
                                varyForward === idx 
                                  ? 'border-blue-600 bg-blue-50 text-blue-700' 
                                  : 'border-gray-300 hover:border-blue-300'
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label>Vary Reverse Primer Location</Label>
                        <div className="flex gap-2 mt-2">
                          {['Off', 'Low', 'Medium', 'High'].map((label, idx) => (
                            <button
                              key={idx}
                              onClick={() => setVaryReverse(idx)}
                              className={`px-3 py-1 border rounded text-sm transition-all ${
                                varyReverse === idx 
                                  ? 'border-blue-600 bg-blue-50 text-blue-700' 
                                  : 'border-gray-300 hover:border-blue-300'
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Force Primer Positions */}
                  <div className="space-y-4 pt-4 border-t">
                    <h3 className="font-semibold text-lg">Force Primer Positions</h3>
                    <p className="text-sm text-gray-600">Optionally force primers to specific positions in the sequence</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Force Forward Start (5' End)</Label>
                        <Input
                          type="number"
                          placeholder="Position (optional)"
                          value={forceForwardStart}
                          onChange={(e) => setForceForwardStart(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Force Forward Stop (3' End)</Label>
                        <Input
                          type="number"
                          placeholder="Position (optional)"
                          value={forceForwardEnd}
                          onChange={(e) => setForceForwardEnd(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Force Reverse Start (5' End)</Label>
                        <Input
                          type="number"
                          placeholder="Position (optional)"
                          value={forceReverseStart}
                          onChange={(e) => setForceReverseStart(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Force Reverse Stop (3' End)</Label>
                        <Input
                          type="number"
                          placeholder="Position (optional)"
                          value={forceReverseEnd}
                          onChange={(e) => setForceReverseEnd(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Regions & Junctions */}
                <TabsContent value="regions" className="space-y-6 mt-4">
                  {/* Custom Target Regions */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Custom Target Regions</h3>
                    
                    {/* Included Region */}
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="use-included"
                        checked={useIncludedRegion}
                        onCheckedChange={setUseIncludedRegion}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <Label htmlFor="use-included">Included Region</Label>
                        <Input
                          placeholder="e.g., 100-1000"
                          value={includedRegion}
                          onChange={(e) => setIncludedRegion(e.target.value)}
                          disabled={!useIncludedRegion}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">Only design primers within this region</p>
                      </div>
                    </div>

                    {/* Excluded Regions */}
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="use-excluded"
                        checked={useExcludedRegions}
                        onCheckedChange={setUseExcludedRegions}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <Label htmlFor="use-excluded">Excluded Regions</Label>
                        <Input
                          placeholder="e.g., 100-199, 901-1000"
                          value={excludedRegions}
                          onChange={(e) => setExcludedRegions(e.target.value)}
                          disabled={!useExcludedRegions}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">Avoid designing primers in these regions</p>
                      </div>
                    </div>

                    {/* Target Regions (Primer Focus) */}
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="use-target"
                        checked={useTargetRegions}
                        onCheckedChange={setUseTargetRegions}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <Label htmlFor="use-target">Target Regions (Primer Focus)</Label>
                        <Input
                          placeholder="e.g., 410-430, 595-615"
                          value={targetRegions}
                          onChange={(e) => setTargetRegions(e.target.value)}
                          disabled={!useTargetRegions}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">Prefer primers that flank these regions</p>
                      </div>
                    </div>

                    {/* Excluded Probe Regions */}
                    {assayType === 'primers_and_probe' && (
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id="use-excluded-probe"
                          checked={useExcludedProbeRegions}
                          onCheckedChange={setUseExcludedProbeRegions}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <Label htmlFor="use-excluded-probe">Excluded Regions for Probe</Label>
                          <Input
                            placeholder="e.g., 445-490, 620-635"
                            value={excludedProbeRegions}
                            onChange={(e) => setExcludedProbeRegions(e.target.value)}
                            disabled={!useExcludedProbeRegions}
                            className="mt-1"
                          />
                          <p className="text-xs text-gray-500 mt-1">Avoid probe design in these regions</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Junction Design */}
                  <div className="space-y-4 pt-4 border-t">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="use-junctions"
                        checked={useJunctions}
                        onCheckedChange={setUseJunctions}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">Design Across Junctions</h3>
                        <p className="text-sm text-gray-600 mb-3">
                          For designs spanning exon-exon or exon-intron junctions
                        </p>
                        
                        <div className="space-y-4">
                          <div>
                            <Label>Junction Positions (comma-separated)</Label>
                            <Input
                              placeholder="e.g., 120, 255, 345"
                              value={junctionList}
                              onChange={(e) => setJunctionList(e.target.value)}
                              disabled={!useJunctions}
                              className="mt-1"
                            />
                          </div>
                          
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <Label>Min Overlap on 3' End</Label>
                              <Input
                                type="number"
                                value={minOverlap3Prime}
                                onChange={(e) => setMinOverlap3Prime(parseInt(e.target.value))}
                                disabled={!useJunctions}
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label>Min Overlap on 5' End</Label>
                              <Input
                                type="number"
                                value={minOverlap5Prime}
                                onChange={(e) => setMinOverlap5Prime(parseInt(e.target.value))}
                                disabled={!useJunctions}
                                className="mt-1"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Design Button */}
          <div className="flex justify-center">
            <Button
              size="lg"
              onClick={handleDesign}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 px-8"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Designing Primers...
                </>
              ) : (
                <>
                  <Dna className="w-5 h-5 mr-2" />
                  Design Primers
                </>
              )}
            </Button>
          </div>

          {/* Results */}
          {results && results.primer_sets && results.primer_sets.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Design Results</CardTitle>
                <CardDescription>
                  Found {results.primer_sets.length} primer set(s) for {results.sequence_name} ({results.sequence_length} bp)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {results.primer_sets.map((primerSet, idx) => (
                  <div key={idx} className="border rounded-lg p-4 bg-gray-50 space-y-4">
                    {/* Header */}
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-lg">Primer Set {primerSet.rank}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                          <span>Quality Score: {primerSet.quality_score.toFixed(1)}/100</span>
                          <span>Penalty: {primerSet.penalty.toFixed(2)}</span>
                          <span>Amplicon: {primerSet.amplicon_size} bp</span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => setSelectedPrimerSet(selectedPrimerSet === idx ? null : idx)}
                        variant={selectedPrimerSet === idx ? "default" : "outline"}
                      >
                        {selectedPrimerSet === idx ? 'Selected' : 'Select to Order'}
                      </Button>
                    </div>

                    {/* Primers and Probe */}
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Forward Primer */}
                      <div className="bg-white p-3 rounded border">
                        <p className="font-medium text-sm text-gray-700 mb-1">Forward Primer</p>
                        <p className="font-mono text-xs bg-blue-50 p-2 rounded break-all">{primerSet.forward_primer}</p>
                        <div className="grid grid-cols-3 gap-2 mt-2 text-xs text-gray-600">
                          <span>Tm: {primerSet.forward_tm.toFixed(1)}°C</span>
                          <span>GC: {primerSet.forward_gc.toFixed(1)}%</span>
                          <span>Len: {primerSet.forward_length} bp</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Position: {primerSet.forward_start}-{primerSet.forward_end}
                        </p>
                      </div>

                      {/* Reverse Primer */}
                      <div className="bg-white p-3 rounded border">
                        <p className="font-medium text-sm text-gray-700 mb-1">Reverse Primer</p>
                        <p className="font-mono text-xs bg-blue-50 p-2 rounded break-all">{primerSet.reverse_primer}</p>
                        <div className="grid grid-cols-3 gap-2 mt-2 text-xs text-gray-600">
                          <span>Tm: {primerSet.reverse_tm.toFixed(1)}°C</span>
                          <span>GC: {primerSet.reverse_gc.toFixed(1)}%</span>
                          <span>Len: {primerSet.reverse_length} bp</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Position: {primerSet.reverse_start}-{primerSet.reverse_end}
                        </p>
                      </div>

                      {/* Probe if present */}
                      {primerSet.probe_sequence && (
                        <div className="md:col-span-2 bg-white p-3 rounded border">
                          <p className="font-medium text-sm text-gray-700 mb-1">TaqMan Probe</p>
                          <p className="font-mono text-xs bg-yellow-50 p-2 rounded break-all">{primerSet.probe_sequence}</p>
                          <div className="grid grid-cols-4 gap-2 mt-2 text-xs text-gray-600">
                            <span>Tm: {primerSet.probe_tm?.toFixed(1)}°C</span>
                            <span>GC: {primerSet.probe_gc?.toFixed(1)}%</span>
                            <span>Len: {primerSet.probe_length} bp</span>
                            <span>Position: {primerSet.probe_start}-{primerSet.probe_end}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Ordering Section (shown when selected) */}
                    {selectedPrimerSet === idx && (
                      <div className="bg-white p-4 rounded border-2 border-blue-200 space-y-4">
                        <h4 className="font-semibold">Order Configuration</h4>
                        
                        {/* Price Breakdown */}
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>Forward Primer ({primerSet.forward_length} bp × €0.7)</span>
                            <span>€{(primerSet.forward_length * 0.7).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Reverse Primer ({primerSet.reverse_length} bp × €0.7)</span>
                            <span>€{(primerSet.reverse_length * 0.7).toFixed(2)}</span>
                          </div>
                          {primerSet.probe_sequence && (
                            <>
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-2 flex-1">
                                  <Checkbox
                                    id={`double-quencher-${idx}`}
                                    checked={doubleQuencher}
                                    onCheckedChange={setDoubleQuencher}
                                  />
                                  <Label htmlFor={`double-quencher-${idx}`} className="text-sm cursor-pointer">
                                    Double Quencher TaqMan Probe (+€50)
                                  </Label>
                                </div>
                                <span>€{doubleQuencher ? '250.00' : '200.00'}</span>
                              </div>
                            </>
                          )}
                          <div className="flex justify-between pt-2 border-t font-semibold">
                            <span>Total Price (per set)</span>
                            <span>€{calculatePrice(primerSet)}</span>
                          </div>
                        </div>

                        {/* Quantity */}
                        <div>
                          <Label>Quantity</Label>
                          <Input
                            type="number"
                            min="1"
                            value={quantity}
                            onChange={(e) => setQuantity(parseInt(e.target.value))}
                            className="mt-1 w-32"
                          />
                        </div>

                        {/* Add to Cart Button */}
                        <Button
                          onClick={() => handleAddToCart(primerSet)}
                          className="w-full bg-blue-600 hover:bg-blue-700"
                        >
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          Add to Cart (€{(calculatePrice(primerSet) * quantity).toFixed(2)})
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default PrimerDesignWizardFull;
